import { useServiceProviderId } from "../../../service-provider/hooks/useServiceProviderId";
import {
  IServicesWizardConfigs,
  IWizardConfig,
  WizardMode,
  SubmitButtonColor,
} from "../../../wizard/types";
import { parseManageServiceToBackEndModel } from "../../helpers/parseServicesToBackEndModel";
import {
  parseManageServiceToFrontEndModel,
  parseViewServiceToFrontEndModel,
} from "../../helpers/parseServicesToFrontEndModel";
import useServicesQueries from "../../hooks/useServicesQueries";
import {
  IGetServiceBackEndModel,
  IGetViewServiceBackEndModel,
} from "../../types";
import { createConfig } from "../create/config";
import { editConfig } from "../edit/config";
import { viewContentConfig } from "../view/config";

async function getCreateInitialData(serviceProviderId) {
  return {
    serviceProviderId,
  };
}

async function getEditInitialData(
  fetchOne: (id: any) => Promise<{ data: IGetServiceBackEndModel }>,
  serviceId: string,
) {
  const { data } = await fetchOne(serviceId);

  return parseManageServiceToFrontEndModel(data);
}

async function getViewData(
  fetchOne: (id: any) => Promise<{ data: IGetViewServiceBackEndModel }>,
  serviceId: string,
) {
  const { data } = await fetchOne(serviceId);

  return parseViewServiceToFrontEndModel(data);
}

export function useServicesConfig(): IServicesWizardConfigs {
  const { create, update, fetchOne, fetchView, deleteOne } =
    useServicesQueries();
  const serviceProviderId = useServiceProviderId();

  const getCreateConfig = (): IWizardConfig => ({
    steps: [createConfig],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Create,
    getInitialData: () => getCreateInitialData(serviceProviderId),
    saveCallback: (frontEndModel) => {
      const parsedData = parseManageServiceToBackEndModel(frontEndModel);
      return create(parsedData);
    },
  });

  const getEditConfig = (id: string): IWizardConfig => ({
    steps: [editConfig],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Create,
    getInitialData: () => getEditInitialData(fetchOne, id),
    saveCallback: (frontEndModel) => {
      const backEndModel = parseManageServiceToBackEndModel(frontEndModel);
      return update(backEndModel);
    },
  });

  const getViewConfig = (id): IWizardConfig => ({
    steps: [viewContentConfig],
    submitButtonText: null,
    cancelButtonText: "Close",
    mode: WizardMode.View,
    getInitialData: () => getViewData(fetchView, id),
  });

  const getDeleteConfig = (id): IWizardConfig => ({
    steps: [
      {
        width: "430px",
        titleConfig: {
          titleText: "Delete service",
        },
        content: "Are you sure you want to delete this service?",
      },
    ],
    mode: WizardMode.Delete,
    submitButtonColor: SubmitButtonColor.Danger,
    submitButtonText: "Delete",
    saveCallback: () => deleteOne(id),
  });

  return {
    getCreateConfig,
    getEditConfig,
    getViewConfig,
    getDeleteConfig,
  } as IServicesWizardConfigs;
}
